import { Magnifer } from '@solar-icons/react';
import { ArrowRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import ProductAvailability from '~/components/product/ProductAvailability';
import { useDropcart } from '~/context/DropcartContext';
import { useFlashMessages } from '~/context/FlashMessagesContext';
import { useOrder } from '~/context/OrderContext';
import { pushSearch } from '~/gtm';
import { useRoutePath } from '~/hooks/useRoutePath';
import { Product } from '~/types/Product';
import { apiFetch } from '~/utils/apiFetch';
import { getProductImages } from '~/utils/images';
import { useFormatPrice } from '~/utils/price';

interface SearchResponse {
  products: Product[];
  pagination: {
    total_items?: number;
  };
}

// Never let the panel collapse to a sliver on a very short viewport — below this it is
// better to overlap the keyboard a little than to show nothing at all.
const DROPDOWN_MIN_HEIGHT = 180;
const DROPDOWN_VIEWPORT_GAP = 16;
// A page scroll only dismisses the panel once it is clearly deliberate; mobile browsers
// fire small scrolls of their own when the keyboard appears and when the page rubber-bands.
const SCROLL_CLOSE_THRESHOLD = 60;
// Scrolls landing right after a visual-viewport resize are the browser reacting to the
// keyboard, not the user scrolling away from the search.
const VIEWPORT_RESIZE_GRACE_MS = 600;

const Search = () => {
  const { t } = useTranslation();
  const routePath = useRoutePath();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useOrder();
  const { openDropcart } = useDropcart();
  const { addMessage } = useFlashMessages();
  const formatPrice = useFormatPrice();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const SEARCH_LIMIT = 4;

  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const lastViewportResizeRef = useRef(0);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  // Close search on route change
  useEffect(() => {
    handleClose();
  }, [location.pathname, handleClose]);

  const handleAddToCart = useCallback(
    async (product: Product) => {
      const variantId = (product.defaultVariantData as { '@id'?: string })?.['@id'];
      const variantCode = variantId?.split('/').pop();

      if (!variantCode) return;

      setAddingToCart(product.id);
      try {
        await addToCart(variantCode, 1);
        addMessage('success', t('product_added_to_cart', 'Produkt byl přidán do košíku'));
        handleClose();
        openDropcart();
      } catch (e) {
        console.error(e);
        addMessage('error', t('add_to_cart_error', 'Nepodařilo se přidat produkt do košíku'));
      } finally {
        setAddingToCart(null);
      }
    },
    [addToCart, addMessage, t, handleClose, openDropcart]
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const main = document.querySelector('main');
    const megamenu = document.querySelector('.megamenu');
    const viewport = window.visualViewport;
    const scrollAnchor = window.scrollY;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    // The on-screen keyboard shrinks the *visual* viewport without changing
    // `innerHeight`, so a viewport-unit cap would still let the panel run underneath it.
    // Measure what is really visible below the input instead; the results then scroll
    // inside the panel rather than disappearing behind the keyboard.
    const updateAvailableHeight = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown) return;

      const viewportHeight = viewport?.height ?? window.innerHeight;
      const viewportOffset = viewport?.offsetTop ?? 0;
      const top = dropdown.getBoundingClientRect().top - viewportOffset;
      const available = viewportHeight - top - DROPDOWN_VIEWPORT_GAP;

      dropdown.style.setProperty('--search-dropdown-max-height', `${Math.max(available, DROPDOWN_MIN_HEIGHT)}px`);
    };

    const handleViewportResize = () => {
      lastViewportResizeRef.current = Date.now();
      updateAvailableHeight();
    };

    const handleScroll = () => {
      // Opening the keyboard makes the browser scroll the focused input into view, and
      // dragging past the page edge rubber-bands it. Neither means "I'm done searching",
      // so ignore both and close only on a scroll that actually moves the page away.
      if (Date.now() - lastViewportResizeRef.current < VIEWPORT_RESIZE_GRACE_MS) return;
      if (Math.abs(window.scrollY - scrollAnchor) < SCROLL_CLOSE_THRESHOLD) return;
      handleClose();
    };

    updateAvailableHeight();

    main?.classList.add('search-overlay-active');
    megamenu?.classList.add('search-overlay-active');
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateAvailableHeight);
    viewport?.addEventListener('resize', handleViewportResize);
    viewport?.addEventListener('scroll', updateAvailableHeight);

    return () => {
      const currentMain = document.querySelector('main');
      const currentMegamenu = document.querySelector('.megamenu');
      currentMain?.classList.remove('search-overlay-active');
      currentMegamenu?.classList.remove('search-overlay-active');
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateAvailableHeight);
      viewport?.removeEventListener('resize', handleViewportResize);
      viewport?.removeEventListener('scroll', updateAvailableHeight);
    };
  }, [isOpen, handleClose]);

  const searchProducts = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);

    try {
      const apiUrl = typeof window !== 'undefined' ? window.ENV?.API_URL : process.env.PUBLIC_API_URL;
      const url = `${apiUrl}/api/v2/shop/products/search?query=${encodeURIComponent(searchQuery)}&limit=${SEARCH_LIMIT}&page=1&imageFilter=sylius_shop_product_small_thumbnail`;

      const response = await apiFetch(url, {
        signal: abortControllerRef.current.signal,
      });

      if (response.ok) {
        const data: SearchResponse = await response.json();
        const products = data.products || [];
        const total = data.pagination?.total_items || 0;
        setResults(products);
        setTotalItems(total);
        setActiveIndex(-1);
        setIsOpen(true);
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Search error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchProducts(value);
    }, 300);
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (activeIndex >= 0 && results[activeIndex]) {
          navigate(routePath('product', results[activeIndex].slug));
          handleClose();
        } else if (query.length >= 2) {
          pushSearch(query);
          navigate(`${routePath('search')}?query=${encodeURIComponent(query)}`);
          handleClose();
        }
        return;
      }

      if (!isOpen || results.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
      }
    },
    [handleClose, isOpen, results, activeIndex, query, navigate, routePath]
  );

  useEffect(() => {
    if (activeIndex >= 0 && resultsRef.current) {
      const activeElement = resultsRef.current.querySelector(`[data-index="${activeIndex}"]`);
      activeElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="search-form flex-grow-1 position-relative">
      <Form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (query.length >= 2) {
            pushSearch(query);
            navigate(`${routePath('search')}?query=${encodeURIComponent(query)}`);
            handleClose();
          }
        }}
      >
        <Form.Group controlId="search" className="position-relative">
          <Form.Label className="visually-hidden">{t('search_placeholder')}</Form.Label>
          <Form.Control
            ref={inputRef}
            type="search"
            name="search"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={t('search_placeholder')}
            autoComplete="off"
            aria-controls={isOpen ? 'search-dropdown' : undefined}
            aria-describedby={query.length > 0 && query.length < 2 ? 'search-min-chars-hint' : undefined}
            className="search-form__input"
          />
          <Button
            type="submit"
            className="search-form__button bg-transparent border-0 h-100 p-0 position-absolute top-0"
            aria-label={t('search_submit', 'Vyhledat')}
          >
            <Magnifer weight="Outline" size={24} aria-hidden="true" />
          </Button>
        </Form.Group>
      </Form>

      {query.length > 0 && query.length < 2 && (
        <div
          id="search-min-chars-hint"
          className="position-absolute bg-white rounded-4 shadow-lg p-3 text-muted small search-dropdown"
          role="status"
          aria-live="polite"
        >
          {t('search_min_chars', 'Zadejte alespoň 2 znaky pro vyhledávání')}
        </div>
      )}

      {isOpen && (
        <div
          ref={dropdownRef}
          id="search-dropdown"
          role="region"
          aria-label={t('search_results', 'Výsledky vyhledávání')}
          className="position-absolute bg-white overflow-hidden search-dropdown"
        >
          <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
            {isLoading
              ? t('search_loading', 'Načítání výsledků...')
              : results.length === 0
                ? t('search_no_results', 'Žádné výsledky')
                : t('search_results_count', 'Nalezeno {{count}} produktů', { count: totalItems })}
          </div>

          {isLoading && (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" size="sm" aria-hidden="true" />
            </div>
          )}

          {!isLoading && results.length === 0 && (
            <div className="p-4 text-center text-muted">{t('search_no_results', 'Žádné výsledky')}</div>
          )}

          {!isLoading && results.length > 0 && (
            <>
              <div className="search-results__header">
                <strong className="search-results__heading" id="search-results-heading">
                  {t('search_found_in_products', 'Nalezeno v produktech')}
                </strong>
              </div>

              <div ref={resultsRef} className="search-results" role="list" aria-labelledby="search-results-heading">
                {results.map((product, index) => (
                  <div
                    key={product.id}
                    id={`search-result-${index}`}
                    data-index={index}
                    role="listitem"
                    className={`search-item${activeIndex === index ? ' search-item--active' : ''}`}
                  >
                    <Link
                      to={routePath('product', product.slug)}
                      className="search-item__link d-flex align-items-center text-decoration-none"
                      onClick={handleClose}
                      aria-current={activeIndex === index ? 'true' : undefined}
                    >
                      {getProductImages(product.images, 'small_thumbnail')[0]?.path && (
                        <img
                          src={getProductImages(product.images, 'small_thumbnail')[0].path}
                          alt={product.name}
                          className="search-item__image"
                          decoding="async"
                        />
                      )}
                      <div className="search-item__info">
                        <div className="search-item__name">{product.name}</div>
                        {product.defaultVariantData?.name && product.defaultVariantData?.name !== product.name && (
                          <div className="search-item__variant">
                            {t('variant', 'variant')}: {product.defaultVariantData.name}
                          </div>
                        )}
                      </div>
                    </Link>

                    {/* Configurable products don't expose availability — hide it entirely. */}
                    {product.customizable !== true && (
                      <div className="search-item__availability">
                        <ProductAvailability
                          // Card availability, derived from the product-level flags:
                          //  - not available         → "Není skladem"
                          //  - available & isStock   → "Skladem"
                          //  - available & !isStock  → stock category label (or a generic "Skladem" when none is set)
                          isAvailable={product.isAvailable ?? false}
                          variant="icon"
                          size="small"
                          stockCategory={
                            (product.isAvailable ?? false) && !(product.isStock ?? false)
                              ? product.defaultVariantData?.stockCategory
                              : undefined
                          }
                        />
                      </div>
                    )}

                    <div className="search-item__price">
                      {product.defaultVariantData?.price && (
                        <div className="search-item__price-amount">
                          {formatPrice(product.defaultVariantData.price, product.defaultVariantData.currency)}
                        </div>
                      )}
                      {!!product.defaultVariantData?.originalPrice &&
                        product.defaultVariantData.originalPrice > product.defaultVariantData.price && (
                          <div className="search-item__price-original">
                            {formatPrice(product.defaultVariantData.originalPrice, product.defaultVariantData.currency)}
                          </div>
                        )}
                    </div>

                    <div className="search-item__action">
                      {product.variants.length > 1 ? (
                        <Link
                          to={routePath('product', product.slug)}
                          className="search-item__btn btn btn-outline-dark"
                          onClick={handleClose}
                          aria-label={t('view_product_variants', 'Zobrazit varianty produktu {{name}}', {
                            name: product.name,
                          })}
                        >
                          {t('select_variant', 'Zobrazit')}
                        </Link>
                      ) : (
                        <Button
                          variant="outline-dark"
                          className="search-item__btn"
                          disabled={addingToCart === product.id}
                          aria-busy={addingToCart === product.id}
                          aria-label={t('add_product_to_cart', 'Přidat {{name}} do košíku', { name: product.name })}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCart(product);
                          }}
                        >
                          {addingToCart === product.id ? (
                            <>
                              <Spinner animation="border" size="sm" aria-hidden="true" />
                              <span className="visually-hidden">{t('adding_to_cart', 'Přidávám do košíku...')}</span>
                            </>
                          ) : (
                            t('add_to_cart', 'Do košíku')
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {totalItems > SEARCH_LIMIT && (
                <div className="search-show-all">
                  <Button
                    variant="dark"
                    className="search-show-all__btn"
                    onClick={() => {
                      pushSearch(query);
                      navigate(`${routePath('search')}?query=${encodeURIComponent(query)}`);
                      handleClose();
                    }}
                    aria-label={t('show_all_search_results', 'Zobrazit všech {{count}} produktů pro "{{query}}"', {
                      count: totalItems,
                      query,
                    })}
                  >
                    <span>
                      {t('show_all_products', 'Zobrazit všechny produkty')} ({totalItems})
                    </span>
                    <ArrowRight size={20} className="ms-2" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Search;
